'use client';

import { Plus, Trash2 } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

const GRADE_POINTS: Record<string, number> = {
  'A+': 4.0,
  A: 4.0,
  'A-': 3.7,
  'B+': 3.3,
  B: 3.0,
  'B-': 2.7,
  'C+': 2.3,
  C: 2.0,
  'C-': 1.7,
  'D+': 1.3,
  D: 1.0,
  F: 0.0,
};

const GRADE_OPTIONS = Object.keys(GRADE_POINTS);

interface Course {
  id: string;
  name: string;
  credits: string;
  grade: string;
}

function newCourse(id: string): Course {
  return { id, name: '', credits: '3', grade: 'A' };
}

export function CgpaCalculator() {
  const uid = useId();
  const [courses, setCourses] = useState<Course[]>([
    { id: `${uid}-1`, name: '', credits: '3', grade: 'A' },
    { id: `${uid}-2`, name: '', credits: '3', grade: 'B+' },
  ]);

  function addCourse() {
    setCourses((prev) => [...prev, newCourse(`${uid}-${prev.length + 1}-${Date.now()}`)]);
  }

  function removeCourse(id: string) {
    setCourses((prev) => prev.filter((c) => c.id !== id));
  }

  function updateCourse(id: string, patch: Partial<Course>) {
    setCourses((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }

  const { cgpa, totalCredits } = useMemo(() => {
    let points = 0;
    let credits = 0;
    for (const course of courses) {
      const c = Number.parseFloat(course.credits);
      if (!Number.isFinite(c) || c <= 0) continue;
      points += c * (GRADE_POINTS[course.grade] ?? 0);
      credits += c;
    }
    return { cgpa: credits > 0 ? points / credits : 0, totalCredits: credits };
  }, [courses]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="grid grid-cols-[1fr_5rem_5rem_2.5rem] gap-2 text-xs font-medium text-muted-foreground">
        <span>Course (optional)</span>
        <span>Credits</span>
        <span>Grade</span>
        <span />
      </div>

      <div className="mt-2 space-y-2">
        {courses.map((course) => (
          <div key={course.id} className="grid grid-cols-[1fr_5rem_5rem_2.5rem] items-center gap-2">
            <input
              type="text"
              placeholder="e.g. Calculus I"
              name={`course-name-${course.id}`}
              autoComplete="off"
              value={course.name}
              onChange={(e) => updateCourse(course.id, { name: e.target.value })}
              className="h-9 rounded-lg border border-input bg-background px-2.5 text-sm text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
            />
            <input
              type="number"
              min="0"
              step="0.5"
              name={`course-credits-${course.id}`}
              autoComplete="off"
              value={course.credits}
              onChange={(e) => updateCourse(course.id, { credits: e.target.value })}
              className="h-9 rounded-lg border border-input bg-background px-2.5 text-sm text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
            />
            <select
              name={`course-grade-${course.id}`}
              value={course.grade}
              onChange={(e) => updateCourse(course.id, { grade: e.target.value })}
              className="h-9 rounded-lg border border-input bg-background px-2 text-sm text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
            >
              {GRADE_OPTIONS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => removeCourse(course.id)}
              disabled={courses.length <= 1}
              aria-label="Remove course"
              className="inline-flex size-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-destructive disabled:opacity-30"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addCourse}
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-1.5 text-sm text-muted-foreground hover:border-primary hover:text-primary"
      >
        <Plus className="size-4" /> Add course
      </button>

      <div className="mt-6 flex items-center justify-between rounded-xl bg-primary/10 px-4 py-4">
        <div>
          <p className="text-xs font-medium text-muted-foreground">Your CGPA (4.0 scale)</p>
          <p className="text-3xl font-bold text-primary">{cgpa.toFixed(2)}</p>
        </div>
        <p className="text-sm text-muted-foreground">{totalCredits} credit hours</p>
      </div>
    </div>
  );
}
